/**
 * ServersOfflineBadge — the rail's alarm that every server in the organization is offline. It
 * opens a dialog listing them, each with a Detach button that removes the cloud-side binding so
 * the operator can re-pair the server with a fresh code.
 */
import { CloudOff, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { api } from '@/api';
import { Hint } from '@/components/occurra/Hint';
import { NavRailBadge } from '@/components/occurra/NavRailBadge';
import { StatusBadge } from '@/components/occurra/StatusBadge';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/shadcn/alert-dialog';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog';
import { useIsStableFor } from '@/hooks/useIsStableFor';
import { useServers } from '@/hooks/useServers';
import { getFailureText } from '@/lib/failureMessage';
import { SECOND_MS } from '@/lib/timeUnits';
import type { Server } from '@/types/servers';
import { StatusTone } from '@/types/status';

/**
 * How long every server must have been offline before the rail says so. A page load lists the
 * servers before their statuses arrive, and the alarm must not flash in between.
 */
export const ALL_OFFLINE_BANNER_DELAY_MS = 15 * SECOND_MS;

const SERVER_ROW = 'flex items-center gap-3 rounded border border-occ-border-1' +
    ' bg-occ-background-main px-3 py-2';
const SERVER_ID = 'truncate font-mono text-xs text-occ-foreground-tertiary';
const DETACH_BUTTON = 'inline-flex items-center gap-1 rounded border border-occ-border-1' +
    ' px-2.5 py-1 text-xs font-medium text-occ-red-1000 not-disabled:hover:bg-occ-red-50' +
    ' disabled:text-occ-foreground-disabled';
const REFRESH_BUTTON = 'rounded border border-occ-border-1 px-3 py-1.5 text-sm' +
    ' not-disabled:hover:bg-occ-foreground-25 active:bg-occ-foreground-50';
// As the accent Button: the fill is already the darkest step, so the hover lightens it.
const CLOSE_BUTTON = 'rounded bg-occ-foreground-high-contrast px-3 py-1.5 text-sm font-medium' +
    ' text-occ-foreground-inverted not-disabled:hover:bg-occ-foreground-main';
const DETACHING_REASON = 'Detaching the server';

interface Props {
    isLabelled: boolean;
    isRoomy?: boolean;
}

export const ServersOfflineBadge = ({ isLabelled, isRoomy = false }: Props): ReactNode => {
    const { servers, onlineCount, refetch } = useServers();
    const [isOpen, setOpen] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState<Server | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const isAllOffline = useIsStableFor(onlineCount === 0, ALL_OFFLINE_BANNER_DELAY_MS);

    const onDetach = async (): Promise<void> => {
        if (!confirmTarget) {
            return;
        }
        setBusyId(confirmTarget.id);
        try {
            await api.removeServer(confirmTarget.id);
            toast.success(`Detached ${confirmTarget.name || confirmTarget.id}`);
            setConfirmTarget(null);
            await refetch();
        } catch (error) {
            const reason = getFailureText(error);
            toast.error(`Detach failed: ${reason}`);
        } finally {
            setBusyId(null);
        }
    };

    const isSingle = servers.length === 1;
    const label = isSingle ? 'Server offline' : 'Servers offline';
    const detail = isSingle
        ? `${label}: the organization's only server is unreachable. Open to detach it.`
        : `${label}: every server in this organization is unreachable. Open to detach one.`;
    const title = isSingle ? 'Server is offline' : 'All servers are offline';
    const isDetaching = busyId === confirmTarget?.id;

    if (!isAllOffline) {
        return null;
    }

    return (
        <>
            <NavRailBadge
                icon={CloudOff}
                label={label}
                detail={detail}
                tone={StatusTone.Danger}
                isLabelled={isLabelled}
                isRoomy={isRoomy}
                onClick={(): void => setOpen(true)}
            />

            <Dialog
                open={isOpen}
                onOpenChange={setOpen}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>
                            None of the servers in this organization are currently
                            reachable from the cloud. Detach a server to remove its
                            cloud binding so you can pair it with a fresh code.
                        </DialogDescription>
                    </DialogHeader>

                    <ul className="flex min-w-0 flex-col gap-2">
                        {servers.map((server: Server) => (
                            <li
                                key={server.id}
                                className={SERVER_ROW}
                            >
                                <div className="flex min-w-0 flex-1 flex-col">
                                    <span className="truncate text-sm font-medium">
                                        {server.name || 'Server'}
                                    </span>
                                    <span className={SERVER_ID}>{server.id}</span>
                                </div>
                                <StatusBadge tone={StatusTone.Danger}>Offline</StatusBadge>
                                <Hint label={busyId === server.id ? DETACHING_REASON : undefined}>
                                    <button
                                        type="button"
                                        onClick={(): void => setConfirmTarget(server)}
                                        disabled={busyId === server.id}
                                        className={DETACH_BUTTON}
                                    >
                                        {busyId === server.id && (
                                            <Loader2 className="size-3 animate-spin" />
                                        )}
                                        Detach
                                    </button>
                                </Hint>
                            </li>
                        ))}
                    </ul>

                    <DialogFooter className="gap-2">
                        <button
                            type="button"
                            onClick={(): void => {
                                refetch();
                            }}
                            className={REFRESH_BUTTON}
                        >
                            Refresh
                        </button>
                        <button
                            type="button"
                            onClick={(): void => setOpen(false)}
                            className={CLOSE_BUTTON}
                        >
                            Close
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={!!confirmTarget}
                onOpenChange={(isOpen: boolean): void => {
                    if (!isOpen) {
                        setConfirmTarget(null);
                    }
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Detach this server from cloud?</AlertDialogTitle>
                        <AlertDialogDescription>
                            The cloud-side binding for
                            {' '}
                            <strong>{confirmTarget?.name || confirmTarget?.id}</strong>
                            {' '}
                            will
                            be removed. Recordings and devices remain on the media_server;
                            you'll be able to re-pair it with a fresh code.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <Hint label={isDetaching ? DETACHING_REASON : undefined}>
                            <AlertDialogCancel disabled={isDetaching}>Cancel</AlertDialogCancel>
                        </Hint>
                        <Hint label={isDetaching ? DETACHING_REASON : undefined}>
                            <AlertDialogAction
                                onClick={(event): void => {
                                    event.preventDefault();
                                    onDetach();
                                }}
                                disabled={isDetaching}
                                variant="destructive"
                            >
                                {isDetaching ? 'Detaching…' : 'Detach'}
                            </AlertDialogAction>
                        </Hint>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
